import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FiSave,
  FiEdit,
  FiUploadCloud,
  FiPlus,
  FiTrash2,
} from "react-icons/fi";

const CombinedContentManager = () => {
  const [activeTab, setActiveTab] = useState("hero");

  // ---------------- HERO STATE (updated to new model) ----------------
  const [hero, setHero] = useState({
    heading: "",
    subHeading: "",
    media: {
      type: "video", // "video" or "image"
      videoUrl: "",
      images: [],
    },
  });

  const [initialHero, setInitialHero] = useState({
    heading: "",
    subHeading: "",
    media: {
      type: "video",
      videoUrl: "",
      images: [],
    },
  });

  const [heroLoading, setHeroLoading] = useState(false);
  const [heroSaving, setHeroSaving] = useState(false);
  const [heroEditing, setHeroEditing] = useState(false);
  const [heroMessage, setHeroMessage] = useState(null);
  const [heroUploading, setHeroUploading] = useState(false);

  // ---------------- ABOUT STATE (unchanged) ----------------
  const [aboutData, setAboutData] = useState({
    title: "",
    description: "",
    subText: "",
    conclusionText: "",
    videoUrl: "",
    features: [],
  });
  const [initialAboutData, setInitialAboutData] = useState({
    title: "",
    description: "",
    subText: "",
    conclusionText: "",
    videoUrl: "",
    features: [],
  });
  const [aboutLoading, setAboutLoading] = useState(false);
  const [aboutSaving, setAboutSaving] = useState(false);
  const [aboutEditing, setAboutEditing] = useState(false);
  const [aboutMessage, setAboutMessage] = useState(null);
  const [aboutUploading, setAboutUploading] = useState(false);

  const MAX_VIDEO_SIZE_MB = 25;

  // ---------------- Fetch Hero Content ----------------
  useEffect(() => {
    const fetchHero = async () => {
      try {
        setHeroLoading(true);
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/hero-contents/get-hero`
        );

        if (data?.hero) {
          const h = data.hero;
          // normalize media shape if backend missing fields
          const media = h.media || { type: "video", videoUrl: "", images: [] };

          setHero({
            heading: h.heading || "",
            subHeading: h.subHeading || "",
            media: {
              type: media.type || "video",
              videoUrl: media.videoUrl || "",
              images: Array.isArray(media.images) ? media.images : [],
            },
          });

          setInitialHero({
            heading: h.heading || "",
            subHeading: h.subHeading || "",
            media: {
              type: media.type || "video",
              videoUrl: media.videoUrl || "",
              images: Array.isArray(media.images) ? media.images : [],
            },
          });
        }
      } catch (err) {
        console.error("Fetch hero error:", err);
      } finally {
        setHeroLoading(false);
      }
    };
    fetchHero();
  }, []);

  // ---------------- Fetch About Content ----------------
  useEffect(() => {
    const fetchAbout = async () => {
      try {
        setAboutLoading(true);
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/whoarewe/getabout`
        );
        if (data) {
          // backend shape previously used; keep as-is
          setAboutData(data);
          setInitialAboutData(data);
        }
      } catch (err) {
        console.error("Fetch about error:", err);
      } finally {
        setAboutLoading(false);
      }
    };
    fetchAbout();
  }, []);

  // ---------------- HERO UPLOADS ----------------

  // Video upload — replaces media with video type
  const handleHeroVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("video/")) {
      setHeroMessage("Please upload a valid video file.");
      return;
    }
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_VIDEO_SIZE_MB) {
      setHeroMessage(`Video must be less than ${MAX_VIDEO_SIZE_MB} MB.`);
      return;
    }

    try {
      setHeroUploading(true);
      setHeroMessage("Uploading video...");

      const formData = new FormData();
      formData.append("file", file);
      formData.append(
        "upload_preset",
        process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
      );

      const uploadResponse = await axios.post(
        process.env.REACT_APP_CLOUDINARY_IMAGE_UPLOAD_URL,
        formData
      );

      const videoUrl = uploadResponse.data.secure_url;
      setHero({
        ...hero,
        media: { type: "video", videoUrl, images: [] },
      });
      setHeroMessage("Video uploaded successfully ✅");
    } catch (error) {
      console.error("Video upload error:", error);
      setHeroMessage("Video upload failed. Please try again.");
    } finally {
      setHeroUploading(false);
      setTimeout(() => setHeroMessage(null), 5000);
    }
  };

  // Images upload — accepts multiple files and appends to images array, sets media.type to "image"
  const handleHeroImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // optional: filter non-image files
    const invalid = files.some((f) => !f.type.startsWith("image/"));
    if (invalid) {
      setHeroMessage("Only image files are allowed for banners.");
      return;
    }

    try {
      setHeroUploading(true);
      setHeroMessage("Uploading images...");

      const uploadedImages = [];

      // sequential upload (keeps code simple). For many images you may want Promise.all
      for (let file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append(
          "upload_preset",
          process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
        );

        const uploadResponse = await axios.post(
          process.env.REACT_APP_CLOUDINARY_IMAGE_UPLOAD_URL,
          formData
        );

        uploadedImages.push(uploadResponse.data.secure_url);
      }

      setHero((prev) => ({
        ...prev,
        media: {
          type: "image",
          videoUrl: "",
          images: [...(prev.media.images || []), ...uploadedImages],
        },
      }));

      setHeroMessage("Images uploaded successfully ✅");
    } catch (error) {
      console.error("Image upload error:", error);
      setHeroMessage("Image upload failed. Please try again.");
    } finally {
      setHeroUploading(false);
      setTimeout(() => setHeroMessage(null), 5000);
    }
  };

  const removeHeroImage = (index) => {
    setHero((prev) => {
      const updated = prev.media.images.filter((_, i) => i !== index);
      return {
        ...prev,
        media: { ...prev.media, images: updated },
      };
    });
  };

  // ---------------- SAVE HERO ----------------
  const handleHeroSave = async (e) => {
    e.preventDefault();
    try {
      setHeroSaving(true);

      // build payload that matches backend schema
      const payload = {
        heading: hero.heading,
        subHeading: hero.subHeading,
        media: {
          type: hero.media.type,
          videoUrl: hero.media.type === "video" ? hero.media.videoUrl : undefined,
          images:
            hero.media.type === "image" ? hero.media.images : undefined,
        },
      };

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/hero-contents/upsert-hero`,
        payload
      );

      setHeroMessage(data.message || "Hero saved successfully.");
      // update initial to current to reset "changed" detection
      setInitialHero({
        heading: hero.heading,
        subHeading: hero.subHeading,
        media: {
          type: hero.media.type,
          videoUrl: hero.media.videoUrl,
          images: [...(hero.media.images || [])],
        },
      });

      setHeroEditing(false);
      setTimeout(() => setHeroMessage(null), 5000);
    } catch (err) {
      console.error("Hero save error:", err);
      setHeroMessage(err.response?.data?.message || "Something went wrong.");
    } finally {
      setHeroSaving(false);
    }
  };

  // ---------------- ABOUT UPLOAD & SAVE (unchanged) ----------------
  const handleAboutVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("video/")) {
      setAboutMessage("Please upload a valid video file.");
      return;
    }
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_VIDEO_SIZE_MB) {
      setAboutMessage(`Video must be less than ${MAX_VIDEO_SIZE_MB} MB.`);
      return;
    }

    try {
      setAboutUploading(true);
      setAboutMessage("Uploading video...");

      const formData = new FormData();
      formData.append("file", file);
      formData.append(
        "upload_preset",
        process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
      );

      const uploadResponse = await axios.post(
        process.env.REACT_APP_CLOUDINARY_IMAGE_UPLOAD_URL,
        formData
      );

      const videoUrl = uploadResponse.data.secure_url;
      setAboutData({ ...aboutData, videoUrl: videoUrl });
      setAboutMessage("Video uploaded successfully ✅");
    } catch (error) {
      console.error("About video upload error:", error);
      setAboutMessage("Video upload failed. Please try again.");
    } finally {
      setAboutUploading(false);
      setTimeout(() => setAboutMessage(null), 5000);
    }
  };

  const handleAboutSave = async (e) => {
    e.preventDefault();
    try {
      setAboutSaving(true);
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/whoarewe/update`,
        aboutData
      );
      setAboutMessage(data.message || "Content updated successfully!");
      setInitialAboutData(aboutData);
      setAboutEditing(false);
      setTimeout(() => setAboutMessage(null), 5000);
    } catch (err) {
      console.error("About save error:", err);
      setAboutMessage(err.response?.data?.message || "Something went wrong.");
    } finally {
      setAboutSaving(false);
    }
  };

  // ---------------- ABOUT FEATURES ----------------
  const addFeature = () => {
    setAboutData((prev) => ({ ...prev, features: [...(prev.features || []), { text: "" }] }));
  };

  const removeFeature = (index) => {
    const updatedFeatures = aboutData.features.filter((_, i) => i !== index);
    setAboutData({ ...aboutData, features: updatedFeatures });
  };

  const updateFeature = (index, value) => {
    const updatedFeatures = [...aboutData.features];
    updatedFeatures[index] = { text: value };
    setAboutData({ ...aboutData, features: updatedFeatures });
  };

  // ---------------- CHANGE DETECTION ----------------
  const heroHasChanges =
    hero.heading !== initialHero.heading ||
    hero.subHeading !== initialHero.subHeading ||
    hero.media.type !== initialHero.media.type ||
    (hero.media.type === "video" && hero.media.videoUrl !== initialHero.media.videoUrl) ||
    (hero.media.type === "image" &&
      JSON.stringify(hero.media.images || []) !== JSON.stringify(initialHero.media.images || []));

  const aboutHasChanges = JSON.stringify(aboutData) !== JSON.stringify(initialAboutData);

  // ---------------- RENDER ----------------
  return (
    <div className="min-h-screen bg-transparent p-6 w-full">
      <h1 className="text-3xl font-heading font-bold mb-8 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent inline-block">
        Manage Website Content
      </h1>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("hero")}
          className={`px-6 py-3 font-medium transition-all ${
            activeTab === "hero"
              ? "text-primaryBtnColor border-b-2 border-primaryBtnColor"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Hero Section
        </button>
        <button
          onClick={() => setActiveTab("about")}
          className={`px-6 py-3 font-medium transition-all ${
            activeTab === "about"
              ? "text-primaryBtnColor border-b-2 border-primaryBtnColor"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          About Us
        </button>
      </div>

      {/* Hero Content */}
      {activeTab === "hero" && (
        <div>
          {heroLoading ? (
            <div className="bg-white rounded-2xl space-y-6 animate-pulse">
              <div className="h-4 w-32 bg-gray-300 rounded mb-2"></div>
              <div className="h-10 w-full bg-gray-200 rounded"></div>
            </div>
          ) : (
            <form onSubmit={handleHeroSave} className="bg-white rounded-2xl space-y-6 max-w-full p-6">
              {/* Heading */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Heading
                </label>
                <input
                  type="text"
                  value={hero.heading}
                  onChange={(e) => setHero({ ...hero, heading: e.target.value })}
                  className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
                  placeholder="Enter hero heading"
                  required
                  disabled={!heroEditing}
                />
              </div>

              {/* Subheading */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sub Heading
                </label>
                <textarea
                  value={hero.subHeading}
                  onChange={(e) => setHero({ ...hero, subHeading: e.target.value })}
                  className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
                  placeholder="Enter hero subheading"
                  rows={4}
                  required
                  disabled={!heroEditing}
                />
              </div>

              {/* Media Type Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Media Type
                </label>
                <select
                  disabled={!heroEditing}
                  value={hero.media.type}
                  onChange={(e) =>
                    setHero({
                      ...hero,
                      media: { type: e.target.value, videoUrl: "", images: [] },
                    })
                  }
                  className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
                >
                  <option value="video">Video</option>
                  <option value="image">Images (Carousel)</option>
                </select>
              </div>

              {/* VIDEO SECTION */}
              {hero.media.type === "video" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Video (Max {MAX_VIDEO_SIZE_MB} MB)
                  </label>

                  {hero.media.videoUrl ? (
                    <video
                      src={hero.media.videoUrl}
                      controls
                      className="w-full rounded-lg border border-gray-300 mb-3"
                    />
                  ) : (
                    <p className="text-sm text-gray-500 mb-3">No video uploaded yet.</p>
                  )}

                  {heroEditing && (
                    <label className="flex items-center justify-center w-full border-2 border-dashed border-gray-400 rounded-lg py-4 cursor-pointer hover:border-black transition">
                      <FiUploadCloud className="text-2xl mr-2" />
                      <span className="text-gray-700 font-medium">
                        {heroUploading ? "Uploading..." : "Click to upload video"}
                      </span>
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={handleHeroVideoUpload}
                        disabled={heroUploading}
                      />
                    </label>
                  )}
                </div>
              )}

              {/* IMAGE CAROUSEL MANAGEMENT */}
              {hero.media.type === "image" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banner Images (Carousel)
                  </label>

                  {/* Preview Images */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-3">
                    {(hero.media.images || []).map((img, index) => (
                      <div key={index} className="relative">
                        <img
                          src={img}
                          alt={`banner-${index}`}
                          className="w-full h-32 object-cover rounded-lg border"
                        />
                        {heroEditing && (
                          <button
                            type="button"
                            onClick={() => removeHeroImage(index)}
                            className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full"
                            aria-label={`Remove image ${index + 1}`}
                          >
                            <FiTrash2 size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Upload */}
                  {heroEditing && (
                    <label className="flex items-center justify-center w-full border-2 border-dashed border-gray-400 rounded-lg py-4 cursor-pointer hover:border-black transition">
                      <FiUploadCloud className="text-2xl mr-2" />
                      <span className="text-gray-700 font-medium">
                        {heroUploading ? "Uploading..." : "Upload Images"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleHeroImageUpload}
                        disabled={heroUploading}
                      />
                    </label>
                  )}
                </div>
              )}

              {/* Buttons */}
              <div className={`w-full flex items-center ${heroMessage ? "justify-between" : "justify-end"} gap-4`}>
                {heroMessage && <p className="text-sm text-green-600 font-medium">{heroMessage}</p>}

                {!heroEditing ? (
                  <button
                    type="button"
                    onClick={() => setHeroEditing(true)}
                    className="w-52 px-6 py-2 rounded-xl shadow-md font-medium 
                     text-gray-800 bg-gradient-to-r from-gray-100 to-gray-300
                     hover:from-gray-200 hover:to-gray-400 
                     active:scale-95 transition-all duration-200 ease-in-out
                     flex items-center justify-center gap-2"
                  >
                    <FiEdit className="text-lg text-gray-600" />
                    Edit Details
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={heroSaving || !heroHasChanges}
                    className="flex items-center justify-center w-52 gap-2 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white px-6 py-2 rounded-lg shadow-lg hover:opacity-90 transition disabled:opacity-50"
                  >
                    {heroSaving ? (
                      <div className="spinner w-full"></div>
                    ) : (
                      <>
                        <FiSave size={18} />
                        Save Changes
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      )}

      {/* About Content (unchanged UI and logic) */}
      {activeTab === "about" && (
        <div>
          {aboutLoading ? (
            <div className="bg-white rounded-2xl space-y-6 animate-pulse">
              <div className="h-4 w-32 bg-gray-300 rounded mb-2"></div>
              <div className="h-10 w-full bg-gray-200 rounded"></div>
            </div>
          ) : (
            <form
              onSubmit={handleAboutSave}
              className="bg-white rounded-2xl p-6 space-y-6 max-w-full"
            >
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={aboutData.title}
                  onChange={(e) =>
                    setAboutData({ ...aboutData, title: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
                  placeholder="Enter title"
                  required
                  disabled={!aboutEditing}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={aboutData.description}
                  onChange={(e) =>
                    setAboutData({ ...aboutData, description: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
                  placeholder="Enter description"
                  rows={4}
                  required
                  disabled={!aboutEditing}
                />
              </div>

              {/* Sub Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Feature Intro
                </label>
                <input
                  type="text"
                  value={aboutData.subText}
                  onChange={(e) =>
                    setAboutData({ ...aboutData, subText: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
                  placeholder="Enter sub text"
                  required
                  disabled={!aboutEditing}
                />
              </div>

              {/* Features */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Features
                  </label>
                  {aboutEditing && (
                    <button
                      type="button"
                      onClick={addFeature}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      <FiPlus size={16} />
                      Add Feature
                    </button>
                  )}
                </div>
                <div className="space-y-3">
                  {aboutData.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={feature.text}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        className="flex-1 px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
                        placeholder={`Feature ${index + 1}`}
                        required
                        disabled={!aboutEditing}
                      />
                      {aboutEditing && (
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Conclusion Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conclusion Text
                </label>
                <textarea
                  value={aboutData.conclusionText}
                  onChange={(e) =>
                    setAboutData({
                      ...aboutData,
                      conclusionText: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
                  placeholder="Enter conclusion text"
                  rows={3}
                  required
                  disabled={!aboutEditing}
                />
              </div>

              {/* Background Video Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video (Max {MAX_VIDEO_SIZE_MB} MB)
                </label>

                {aboutData.videoUrl ? (
                  <video
                    src={aboutData.videoUrl}
                    controls
                    className="w-full rounded-lg border border-gray-300 mb-3"
                  />
                ) : (
                  <p className="text-sm text-gray-500 mb-3">
                    No video uploaded yet.
                  </p>
                )}

                {aboutEditing && (
                  <label className="flex items-center justify-center w-full border-2 border-dashed border-gray-400 rounded-lg py-4 cursor-pointer hover:border-black transition">
                    <FiUploadCloud className="text-2xl mr-2" />
                    <span className="text-gray-700 font-medium">
                      {aboutUploading ? "Uploading..." : "Click to upload video"}
                    </span>
                    <input
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={handleAboutVideoUpload}
                      disabled={aboutUploading}
                    />
                  </label>
                )}
              </div>

              {/* Buttons */}
              <div
                className={`w-full flex items-center ${
                  aboutMessage ? "justify-between" : "justify-end"
                } gap-4`}
              >
                {aboutMessage && (
                  <p className="text-sm text-green-600 font-medium">
                    {aboutMessage}
                  </p>
                )}
                {!aboutEditing ? (
                  <button
                    type="button"
                    onClick={() => setAboutEditing(true)}
                    className="w-52 px-6 py-2 rounded-xl shadow-md font-medium 
                     text-gray-800 bg-gradient-to-r from-gray-100 to-gray-300
                     hover:from-gray-200 hover:to-gray-400 
                     active:scale-95 transition-all duration-200 ease-in-out
                     flex items-center justify-center gap-2"
                  >
                    <FiEdit className="text-lg text-gray-600" />
                    Edit Details
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={aboutSaving || !aboutHasChanges}
                    className="flex items-center justify-center w-52 gap-2 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white px-6 py-2 rounded-lg shadow-lg hover:opacity-90 transition disabled:opacity-50"
                  >
                    {aboutSaving ? (
                      <div className="spinner w-full"></div>
                    ) : (
                      <>
                        <FiSave size={18} />
                        Save Changes
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default CombinedContentManager;









// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import {
//   FiSave,
//   FiEdit,
//   FiUploadCloud,
//   FiPlus,
//   FiTrash2,
// } from "react-icons/fi";

// const CombinedContentManager = () => {
//   const [activeTab, setActiveTab] = useState("hero");

//   // Hero State
//   const [hero, setHero] = useState({
//     heading: "",
//     subHeading: "",
//     backgroundVideo: "",
//   });
//   const [initialHero, setInitialHero] = useState({
//     heading: "",
//     subHeading: "",
//     backgroundVideo: "",
//   });
//   const [heroLoading, setHeroLoading] = useState(false);
//   const [heroSaving, setHeroSaving] = useState(false);
//   const [heroEditing, setHeroEditing] = useState(false);
//   const [heroMessage, setHeroMessage] = useState(null);
//   const [heroUploading, setHeroUploading] = useState(false);

//   // About State
//   const [aboutData, setAboutData] = useState({
//     title: "",
//     description: "",
//     subText: "",
//     conclusionText: "",
//     videoUrl: "",
//     features: [],
//   });
//   const [initialAboutData, setInitialAboutData] = useState({
//     title: "",
//     description: "",
//     subText: "",
//     conclusionText: "",
//     videoUrl: "",
//     features: [],
//   });
//   const [aboutLoading, setAboutLoading] = useState(false);
//   const [aboutSaving, setAboutSaving] = useState(false);
//   const [aboutEditing, setAboutEditing] = useState(false);
//   const [aboutMessage, setAboutMessage] = useState(null);
//   const [aboutUploading, setAboutUploading] = useState(false);

//   const MAX_VIDEO_SIZE_MB = 25;

//   // Fetch Hero Content
//   useEffect(() => {
//     const fetchHero = async () => {
//       try {
//         setHeroLoading(true);
//         const { data } = await axios.get(
//           `${process.env.REACT_APP_API_URL}/hero-contents/get-hero`
//         );
//         if (data?.hero) {
//           setHero(data.hero);
//           setInitialHero(data.hero);
//         }
//       } catch (err) {
//         console.error(err);
//       } finally {
//         setHeroLoading(false);
//       }
//     };
//     fetchHero();
//   }, []);

//   // Fetch About Content
//   useEffect(() => {
//     const fetchAbout = async () => {
//       try {
//         setAboutLoading(true);
//         const { data } = await axios.get(
//           `${process.env.REACT_APP_API_URL}/whoarewe/getabout`
//         );
//         if (data) {
//           setAboutData(data);
//           setInitialAboutData(data);
//         }
//       } catch (err) {
//         console.error(err);
//       } finally {
//         setAboutLoading(false);
//       }
//     };
//     fetchAbout();
//   }, []);

//   // Hero Video Upload
//   const handleHeroVideoUpload = async (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     if (!file.type.startsWith("video/")) {
//       setHeroMessage("Please upload a valid video file.");
//       return;
//     }
//     const fileSizeMB = file.size / (1024 * 1024);
//     if (fileSizeMB > MAX_VIDEO_SIZE_MB) {
//       setHeroMessage(`Video must be less than ${MAX_VIDEO_SIZE_MB} MB.`);
//       return;
//     }

//     try {
//       setHeroUploading(true);
//       setHeroMessage("Uploading video...");

//       const formData = new FormData();
//       formData.append("file", file);
//       formData.append(
//         "upload_preset",
//         process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
//       );

//       const uploadResponse = await axios.post(
//         process.env.REACT_APP_CLOUDINARY_IMAGE_UPLOAD_URL,
//         formData
//       );

//       const videoUrl = uploadResponse.data.secure_url;
//       setHero({ ...hero, backgroundVideo: videoUrl });
//       setHeroMessage("Video uploaded successfully ✅");
//     } catch (error) {
//       console.error(error);
//       setHeroMessage("Video upload failed. Please try again.");
//     } finally {
//       setHeroUploading(false);
//       setTimeout(() => setHeroMessage(null), 5000);
//     }
//   };

//   // About Video Upload
//   const handleAboutVideoUpload = async (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     if (!file.type.startsWith("video/")) {
//       setAboutMessage("Please upload a valid video file.");
//       return;
//     }
//     const fileSizeMB = file.size / (1024 * 1024);
//     if (fileSizeMB > MAX_VIDEO_SIZE_MB) {
//       setAboutMessage(`Video must be less than ${MAX_VIDEO_SIZE_MB} MB.`);
//       return;
//     }

//     try {
//       setAboutUploading(true);
//       setAboutMessage("Uploading video...");

//       const formData = new FormData();
//       formData.append("file", file);
//       formData.append(
//         "upload_preset",
//         process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
//       );

//       const uploadResponse = await axios.post(
//         process.env.REACT_APP_CLOUDINARY_IMAGE_UPLOAD_URL,
//         formData
//       );

//       const videoUrl = uploadResponse.data.secure_url;
//       setAboutData({ ...aboutData, videoUrl: videoUrl });
//       setAboutMessage("Video uploaded successfully ✅");
//     } catch (error) {
//       console.error(error);
//       setAboutMessage("Video upload failed. Please try again.");
//     } finally {
//       setAboutUploading(false);
//       setTimeout(() => setAboutMessage(null), 5000);
//     }
//   };

//   // Hero Save
//   const handleHeroSave = async (e) => {
//     e.preventDefault();
//     try {
//       setHeroSaving(true);
//       const { data } = await axios.post(
//         `${process.env.REACT_APP_API_URL}/hero-contents/upsert-hero`,
//         hero
//       );
//       setHeroMessage(data.message);
//       setTimeout(() => setHeroMessage(null), 5000);
//       setInitialHero(hero);
//       setHeroEditing(false);
//     } catch (err) {
//       setHeroMessage(err.response?.data?.message || "Something went wrong.");
//     } finally {
//       setHeroSaving(false);
//     }
//   };

//   // About Save
//   const handleAboutSave = async (e) => {
//     e.preventDefault();
//     try {
//       setAboutSaving(true);
//       const { data } = await axios.post(
//         `${process.env.REACT_APP_API_URL}/whoarewe/update`,
//         aboutData
//       );
//       setAboutMessage(data.message || "Content updated successfully!");
//       setTimeout(() => setAboutMessage(null), 5000);
//       setInitialAboutData(aboutData);
//       setAboutEditing(false);
//     } catch (err) {
//       setAboutMessage(err.response?.data?.message || "Something went wrong.");
//     } finally {
//       setAboutSaving(false);
//     }
//   };

//   // About Features Management
//   const addFeature = () => {
//     setAboutData({
//       ...aboutData,
//       features: [...aboutData.features, { text: "" }],
//     });
//   };

//   const removeFeature = (index) => {
//     const updatedFeatures = aboutData.features.filter((_, i) => i !== index);
//     setAboutData({ ...aboutData, features: updatedFeatures });
//   };

//   const updateFeature = (index, value) => {
//     const updatedFeatures = [...aboutData.features];
//     updatedFeatures[index] = { text: value };
//     setAboutData({ ...aboutData, features: updatedFeatures });
//   };

//   // Detect Changes
//   const heroHasChanges =
//     hero.heading !== initialHero.heading ||
//     hero.subHeading !== initialHero.subHeading ||
//     hero.backgroundVideo !== initialHero.backgroundVideo;

//   const aboutHasChanges =
//     JSON.stringify(aboutData) !== JSON.stringify(initialAboutData);

//   return (
//     <div className="min-h-screen bg-transparent p-6 w-full">
//       <h1 className="text-3xl font-heading font-bold mb-8 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent inline-block">
//         Manage Website Content
//       </h1>

//       {/* Tabs */}
//       <div className="flex gap-4 mb-6 border-b border-gray-200">
//         <button
//           onClick={() => setActiveTab("hero")}
//           className={`px-6 py-3 font-medium transition-all ${
//             activeTab === "hero"
//               ? "text-primaryBtnColor border-b-2 border-primaryBtnColor"
//               : "text-gray-500 hover:text-gray-700"
//           }`}
//         >
//           Hero Section
//         </button>
//         <button
//           onClick={() => setActiveTab("about")}
//           className={`px-6 py-3 font-medium transition-all ${
//             activeTab === "about"
//               ? "text-primaryBtnColor border-b-2 border-primaryBtnColor"
//               : "text-gray-500 hover:text-gray-700"
//           }`}
//         >
//           About Us
//         </button>
//       </div>

//       {/* Hero Content */}
//       {activeTab === "hero" && (
//         <div>
//           {heroLoading ? (
//             <div className="bg-white rounded-2xl space-y-6 animate-pulse">
//               <div className="h-4 w-32 bg-gray-300 rounded mb-2"></div>
//               <div className="h-10 w-full bg-gray-200 rounded"></div>
//             </div>
//           ) : (
//             <form
//               onSubmit={handleHeroSave}
//               className="bg-white rounded-2xl space-y-6 max-w-full"
//             >
//               {/* Heading */}
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Heading
//                 </label>
//                 <input
//                   type="text"
//                   value={hero.heading}
//                   onChange={(e) =>
//                     setHero({ ...hero, heading: e.target.value })
//                   }
//                   className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
//                   placeholder="Enter hero heading"
//                   required
//                   disabled={!heroEditing}
//                 />
//               </div>

//               {/* Subheading */}
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Sub Heading
//                 </label>
//                 <textarea
//                   value={hero.subHeading}
//                   onChange={(e) =>
//                     setHero({ ...hero, subHeading: e.target.value })
//                   }
//                   className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
//                   placeholder="Enter hero subheading"
//                   rows={4}
//                   required
//                   disabled={!heroEditing}
//                 />
//               </div>

//               {/* Background Video Upload */}
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Background Video (Max {MAX_VIDEO_SIZE_MB} MB)
//                 </label>

//                 {hero.backgroundVideo ? (
//                   <video
//                     src={hero.backgroundVideo}
//                     controls
//                     className="w-full rounded-lg border border-gray-300 mb-3"
//                   />
//                 ) : (
//                   <p className="text-sm text-gray-500 mb-3">
//                     No video uploaded yet.
//                   </p>
//                 )}

//                 {heroEditing && (
//                   <label className="flex items-center justify-center w-full border-2 border-dashed border-gray-400 rounded-lg py-4 cursor-pointer hover:border-black transition">
//                     <FiUploadCloud className="text-2xl mr-2" />
//                     <span className="text-gray-700 font-medium">
//                       {heroUploading ? "Uploading..." : "Click to upload video"}
//                     </span>
//                     <input
//                       type="file"
//                       accept="video/*"
//                       className="hidden"
//                       onChange={handleHeroVideoUpload}
//                       disabled={heroUploading}
//                     />
//                   </label>
//                 )}
//               </div>

//               {/* Buttons */}
//               <div
//                 className={`w-full flex items-center ${
//                   heroMessage ? "justify-between" : "justify-end"
//                 } gap-4`}
//               >
//                 {heroMessage && (
//                   <p className="text-sm text-green-600 font-medium">
//                     {heroMessage}
//                   </p>
//                 )}
//                 {!heroEditing ? (
//                   <button
//                     type="button"
//                     onClick={() => setHeroEditing(true)}
//                     className="w-52 px-6 py-2 rounded-xl shadow-md font-medium 
//                      text-gray-800 bg-gradient-to-r from-gray-100 to-gray-300
//                      hover:from-gray-200 hover:to-gray-400 
//                      active:scale-95 transition-all duration-200 ease-in-out
//                      flex items-center justify-center gap-2"
//                   >
//                     <FiEdit className="text-lg text-gray-600" />
//                     Edit Details
//                   </button>
//                 ) : (
//                   <button
//                     type="submit"
//                     disabled={heroSaving || !heroHasChanges}
//                     className="flex items-center justify-center w-52 gap-2 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white px-6 py-2 rounded-lg shadow-lg hover:opacity-90 transition disabled:opacity-50"
//                   >
//                     {heroSaving ? (
//                       <div className="spinner w-full"></div>
//                     ) : (
//                       <>
//                         <FiSave size={18} />
//                         Save Changes
//                       </>
//                     )}
//                   </button>
//                 )}
//               </div>
//             </form>
//           )}
//         </div>
//       )}

//       {/* About Content */}
//       {activeTab === "about" && (
//         <div>
//           {aboutLoading ? (
//             <div className="bg-white rounded-2xl space-y-6 animate-pulse">
//               <div className="h-4 w-32 bg-gray-300 rounded mb-2"></div>
//               <div className="h-10 w-full bg-gray-200 rounded"></div>
//             </div>
//           ) : (
//             <form
//               onSubmit={handleAboutSave}
//               className="bg-white rounded-2xl p-6 space-y-6 max-w-full"
//             >
//               {/* Title */}
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Title
//                 </label>
//                 <input
//                   type="text"
//                   value={aboutData.title}
//                   onChange={(e) =>
//                     setAboutData({ ...aboutData, title: e.target.value })
//                   }
//                   className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
//                   placeholder="Enter title"
//                   required
//                   disabled={!aboutEditing}
//                 />
//               </div>

//               {/* Description */}
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Description
//                 </label>
//                 <textarea
//                   value={aboutData.description}
//                   onChange={(e) =>
//                     setAboutData({ ...aboutData, description: e.target.value })
//                   }
//                   className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
//                   placeholder="Enter description"
//                   rows={4}
//                   required
//                   disabled={!aboutEditing}
//                 />
//               </div>

//               {/* Sub Text */}
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Feature Intro
//                 </label>
//                 <input
//                   type="text"
//                   value={aboutData.subText}
//                   onChange={(e) =>
//                     setAboutData({ ...aboutData, subText: e.target.value })
//                   }
//                   className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
//                   placeholder="Enter sub text"
//                   required
//                   disabled={!aboutEditing}
//                 />
//               </div>

//               {/* Features */}
//               <div>
//                 <div className="flex items-center justify-between mb-2">
//                   <label className="block text-sm font-medium text-gray-700">
//                     Features
//                   </label>
//                   {aboutEditing && (
//                     <button
//                       type="button"
//                       onClick={addFeature}
//                       className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
//                     >
//                       <FiPlus size={16} />
//                       Add Feature
//                     </button>
//                   )}
//                 </div>
//                 <div className="space-y-3">
//                   {aboutData.features.map((feature, index) => (
//                     <div key={index} className="flex items-center gap-2">
//                       <input
//                         type="text"
//                         value={feature.text}
//                         onChange={(e) => updateFeature(index, e.target.value)}
//                         className="flex-1 px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
//                         placeholder={`Feature ${index + 1}`}
//                         required
//                         disabled={!aboutEditing}
//                       />
//                       {aboutEditing && (
//                         <button
//                           type="button"
//                           onClick={() => removeFeature(index)}
//                           className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition"
//                         >
//                           <FiTrash2 size={18} />
//                         </button>
//                       )}
//                     </div>
//                   ))}
//                 </div>
//               </div>

//               {/* Conclusion Text */}
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Conclusion Text
//                 </label>
//                 <textarea
//                   value={aboutData.conclusionText}
//                   onChange={(e) =>
//                     setAboutData({
//                       ...aboutData,
//                       conclusionText: e.target.value,
//                     })
//                   }
//                   className="w-full px-4 py-2 border border-black rounded-lg outline-none focus:ring-1 focus:ring-black"
//                   placeholder="Enter conclusion text"
//                   rows={3}
//                   required
//                   disabled={!aboutEditing}
//                 />
//               </div>

//               {/* Background Video Upload */}
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-2">
//                   Video (Max {MAX_VIDEO_SIZE_MB} MB)
//                 </label>

//                 {aboutData.videoUrl ? (
//                   <video
//                     src={aboutData.videoUrl}
//                     controls
//                     className="w-full rounded-lg border border-gray-300 mb-3"
//                   />
//                 ) : (
//                   <p className="text-sm text-gray-500 mb-3">
//                     No video uploaded yet.
//                   </p>
//                 )}

//                 {aboutEditing && (
//                   <label className="flex items-center justify-center w-full border-2 border-dashed border-gray-400 rounded-lg py-4 cursor-pointer hover:border-black transition">
//                     <FiUploadCloud className="text-2xl mr-2" />
//                     <span className="text-gray-700 font-medium">
//                       {aboutUploading
//                         ? "Uploading..."
//                         : "Click to upload video"}
//                     </span>
//                     <input
//                       type="file"
//                       accept="video/*"
//                       className="hidden"
//                       onChange={handleAboutVideoUpload}
//                       disabled={aboutUploading}
//                     />
//                   </label>
//                 )}
//               </div>

//               {/* Buttons */}
//               <div
//                 className={`w-full flex items-center ${
//                   aboutMessage ? "justify-between" : "justify-end"
//                 } gap-4`}
//               >
//                 {aboutMessage && (
//                   <p className="text-sm text-green-600 font-medium">
//                     {aboutMessage}
//                   </p>
//                 )}
//                 {!aboutEditing ? (
//                   <button
//                     type="button"
//                     onClick={() => setAboutEditing(true)}
//                     className="w-52 px-6 py-2 rounded-xl shadow-md font-medium 
//                      text-gray-800 bg-gradient-to-r from-gray-100 to-gray-300
//                      hover:from-gray-200 hover:to-gray-400 
//                      active:scale-95 transition-all duration-200 ease-in-out
//                      flex items-center justify-center gap-2"
//                   >
//                     <FiEdit className="text-lg text-gray-600" />
//                     Edit Details
//                   </button>
//                 ) : (
//                   <button
//                     type="submit"
//                     disabled={aboutSaving || !aboutHasChanges}
//                     className="flex items-center justify-center w-52 gap-2 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white px-6 py-2 rounded-lg shadow-lg hover:opacity-90 transition disabled:opacity-50"
//                   >
//                     {aboutSaving ? (
//                       <div className="spinner w-full"></div>
//                     ) : (
//                       <>
//                         <FiSave size={18} />
//                         Save Changes
//                       </>
//                     )}
//                   </button>
//                 )}
//               </div>
//             </form>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

// export default CombinedContentManager;
